import React, { useState, useEffect, useContext, useCallback } from 'react';
import { useForm, Controller } from 'react-hook-form';
import {
  Box,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  Chip,
  Autocomplete,
  TextField,
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { AuthContext } from 'context/Auth';
import { db } from 'services/firebase';
import ConfirmDialog from 'components/common/ConfirmDialog';
import MasterChartBackButton from 'components/dashboard/healthRecords/MasterChartBackButton';
import DashboardPageHeader from 'components/common/DashboardPageHeader';
import DataLoadErrorAlert from 'components/common/DataLoadErrorAlert';
import {
  RecordActions,
  RecordCard,
  RecordCardStack,
  joinDetail,
} from 'components/dashboard/healthRecords/RecordList';
import { useAsyncAutocomplete } from 'hooks/useAsyncAutocomplete';
import { conditionsSearchService } from 'services/symptomCheck/conditionsSearchService';

const COMMON_CONDITIONS = [
  'Hypertension', 'Type 2 Diabetes', 'Type 1 Diabetes', 'Asthma', 'COPD',
  'Coronary Artery Disease', 'Heart Failure', 'Atrial Fibrillation',
  'Hyperlipidemia', 'Hypothyroidism', 'Depression', 'Anxiety',
  'GERD', 'Osteoarthritis', 'Rheumatoid Arthritis', 'Chronic Kidney Disease',
  'Migraine', 'Epilepsy', 'Sleep Apnea', 'Obesity',
];

const STATUS_OPTIONS = [
  { value: 'active', label: 'Active', color: 'error' },
  { value: 'controlled', label: 'Controlled', color: 'success' },
  { value: 'resolved', label: 'Resolved', color: undefined },
];

const MedicalConditionsPage = () => {
  const { user } = useContext(AuthContext);

  const [conditions, setConditions] = useState([]);
  const [fullHealthRecords, setFullHealthRecords] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [loadError, setLoadError] = useState(false);
  const [successMsg, setSuccessMsg] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingIndex, setEditingIndex] = useState(-1);
  const [pendingDelete, setPendingDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const { control, handleSubmit, reset, watch, setValue } = useForm({
    defaultValues: { condition: '', conditionCode: '', status: 'active', notes: '' },
  });

  const conditionValue = watch('condition');
  const searchConditions = useCallback(
    (q) => conditionsSearchService.searchConditionsWithCodes(q, 20),
    []
  );
  const { options: conditionOptions, loading: conditionOptionsLoading } =
    useAsyncAutocomplete(conditionValue, searchConditions, { minLength: 2 });

  const load = useCallback(async () => {
    if (!user?.uid) return;
    setLoading(true);
    setLoadError(false);
    try {
      const doc = await db.collection('users').doc(user.uid).get();
      const hr = doc.data()?.healthRecords || {};
      setFullHealthRecords(hr);
      setConditions(hr.pmh || []);
    } catch {
      // An empty list here would read as "none", and adding to it would replace
      // the stored list, so the list and Add stay hidden until a retry succeeds.
      setLoadError(true);
    } finally {
      setLoading(false);
    }
  }, [user?.uid]);

  useEffect(() => {
    load();
  }, [load]);

  const saveToFirestore = useCallback(
    async (updated) => {
      const merged = {
        ...(fullHealthRecords ?? {}),
        pmh: updated,
        updatedAt: new Date().toISOString(),
      };
      await db
        .collection('users')
        .doc(user.uid)
        .set({ healthRecords: merged }, { merge: true });
      setFullHealthRecords(merged);
    },
    [user?.uid, fullHealthRecords]
  );

  const openDialog = (index = -1) => {
    setEditingIndex(index);
    if (index >= 0) {
      const c = conditions[index];
      reset({
        condition: c.condition ?? '',
        conditionCode: c.conditionCode ?? '',
        status: c.status ?? 'active',
        notes: c.notes ?? '',
      });
    } else {
      reset({ condition: '', conditionCode: '', status: 'active', notes: '' });
    }
    setDialogOpen(true);
  };

  const onDialogSubmit = handleSubmit(async (data) => {
    setSaving(true);
    setError(null);
    try {
      const toSave = {
        condition: data.condition.trim(),
        status: data.status,
        notes: data.notes.trim(),
        ...(data.conditionCode ? { conditionCode: data.conditionCode } : {}),
      };
      const updated = [...conditions];
      if (editingIndex >= 0) {
        updated[editingIndex] = toSave;
      } else {
        updated.push(toSave);
      }
      await saveToFirestore(updated);
      setConditions(updated);
      setDialogOpen(false);
      setSuccessMsg(editingIndex >= 0 ? 'Condition updated.' : 'Condition added.');
    } catch {
      setError('Failed to save. Please try again.');
    } finally {
      setSaving(false);
    }
  });

  const handleDelete = async () => {
    setDeleting(true);
    try {
      const updated = conditions.filter((_, i) => i !== pendingDelete.index);
      setConditions(updated);
      await saveToFirestore(updated);
      setSuccessMsg(`Removed "${pendingDelete.label}".`);
      setPendingDelete(null);
    } catch {
      setError('Failed to remove. Please try again.');
      setPendingDelete(null);
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="inner">
      <DashboardPageHeader
        title="Medical Conditions"
        subtitle="Add any ongoing or past conditions, so doctors and SuperIntelligence can take them into account."
      />

      {loadError && <DataLoadErrorAlert message="We couldn't load your medical conditions. Please retry before assuming you have none." onRetry={load} />}
      {error && (
        <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      {successMsg && (
        <Alert severity="success" onClose={() => setSuccessMsg(null)} sx={{ mb: 2 }}>
          {successMsg}
        </Alert>
      )}

      {!loadError && (
        <>

          <RecordCardStack loading={loading}>
            {conditions.map((c, i) => (
              <RecordCard
                key={i}
                title={c.condition}
                detail={joinDetail(
                  STATUS_OPTIONS.find((o) => o.value === c.status)?.label,
                  c.notes
                )}
                onEdit={() => openDialog(i)}
                onDelete={() => setPendingDelete({ index: i, label: c.condition })}
                editLabel={`Edit condition: ${c.condition}`}
                deleteLabel={`Delete condition: ${c.condition}`}
              />
            ))}
          </RecordCardStack>

          <RecordActions
            addLabel="Add a Condition"
            onAdd={() => openDialog()}
            disabled={loading}
            spaced={!loading && conditions.length > 0}
          >
            <MasterChartBackButton />
          </RecordActions>
        </>
      )}

      <Dialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {editingIndex >= 0 ? 'Edit Condition' : 'Add Medical Condition'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Controller
              name="condition"
              control={control}
              rules={{ required: 'Condition name is required' }}
              render={({ field, fieldState: { error: fieldError } }) => (
                <Autocomplete
                  freeSolo
                  options={conditionOptions}
                  loading={conditionOptionsLoading}
                  getOptionLabel={(opt) =>
                    typeof opt === 'string' ? opt : opt?.displayName ?? ''
                  }
                  inputValue={field.value || ''}
                  onInputChange={(_, value, reason) => {
                    if (reason !== 'reset') {
                      field.onChange(value);
                      if (reason === 'clear') setValue('conditionCode', '');
                    }
                  }}
                  onChange={(_, newValue) => {
                    if (!newValue) return;
                    if (typeof newValue === 'string') {
                      field.onChange(newValue);
                    } else {
                      field.onChange(newValue.displayName ?? '');
                      setValue('conditionCode', newValue.conditionCode ?? '');
                    }
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Condition"
                      placeholder="e.g., Hypertension"
                      error={!!fieldError}
                      helperText={
                        fieldError?.message ??
                        'Standard conditions support drug-disease interaction checks.'
                      }
                    />
                  )}
                />
              )}
            />

            <Box>
              <Typography
                variant="caption"
                color="text.secondary"
                display="block"
                gutterBottom
              >
                Common conditions:
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                {COMMON_CONDITIONS.slice(0, 8).map((name) => (
                  <Chip
                    key={name}
                    label={name}
                    size="small"
                    variant="outlined"
                    onClick={() => {
                      setValue('condition', name);
                      setValue('conditionCode', '');
                    }}
                    sx={{ cursor: 'pointer' }}
                  />
                ))}
              </Box>
            </Box>

            <Controller
              name="status"
              control={control}
              render={({ field }) => (
                <FormControl fullWidth>
                  <InputLabel id="status-label">Status</InputLabel>
                  <Select {...field} labelId="status-label" label="Status">
                    {STATUS_OPTIONS.map((o) => (
                      <MenuItem key={o.value} value={o.value}>
                        {o.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}
            />

            <Controller
              name="notes"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Notes"
                  fullWidth
                  multiline
                  rows={2}
                  placeholder="Additional details (optional)"
                />
              )}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <LoadingButton
            variant="contained"
            onClick={onDialogSubmit}
            loading={saving}
            disabled={saving}
          >
            {editingIndex >= 0 ? 'Update' : 'Add'}
          </LoadingButton>
        </DialogActions>
      </Dialog>

      <ConfirmDialog
        open={!!pendingDelete}
        onClose={() => setPendingDelete(null)}
        title="Remove Condition"
        message={`Remove "${pendingDelete?.label}"?`}
        confirmLabel="Remove"
        confirmColor="error"
        loading={deleting}
        onConfirm={handleDelete}
        onExited={() => setPendingDelete(null)}
      />
    </div>
  );
};

export default MedicalConditionsPage;
